import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AgGridReact } from "ag-grid-react";
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-alpine.css";
import "./DataGrid.css";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import CustomButtonComponent from "./CustomButtonComponent";
import CustomDurationColumn from "./CustomDurationColumn";
import {
    convertToMinutes,
    deleteConcertEvent,
    fetchConcertVersion,
    fetchData,
    POLLING_INTERVAL,
    MIN_EVENT_HEIGHT,
    MAX_EVENT_HEIGHT,
    EVENT_DURATION_MULTIPLIER,
} from "./helpers";
import LoadingOverlay from "./LoadingOverlay";
import GridNotification from "./GridNotification/GridNotification";
import { AlertColor } from "@mui/material";
import CustomTrashComponent from "./CustomTrashComponent";

interface DataGridProps {
    startDate: Date;
    concertId: number;
    setCurrentEvent: (event: any) => void;
    onEventClick: (eventName: string, eventType: number, bandId: number, eventId: number) => void;
    onRefresh: () => void;
}

const componentsForGrid = {
    customButton: CustomButtonComponent,
    customDurationColumn: CustomDurationColumn,
    CustomTrashComponent: CustomTrashComponent,
};

const defaultColDef = {
    flex: 1,
    suppressMovable: true,
};

const DataGrid = ({ startDate, concertId, setCurrentEvent, onEventClick }: DataGridProps) => {
    const [rowData, setRowData] = useState<any[]>([]);
    const [concertVersion, setConcertVersion] = useState<number>(0);
    const [gridApi, setGridApi] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [currentDateTime, setCurrentDateTime] = useState<Date>(new Date());
    const [currentPosition, setCurrentPosition] = useState<number>(-1);
    const [open, setOpen] = useState(false);
    const [notificationMsg, setNotificationMsg] = useState("");
    const [severity, setSeverity] = useState<AlertColor>("success");
    const axiosPrivate = useAxiosPrivate();

    // Fetch / refetch data from the server
    const fetchGridData = async () => {
        try {
            const { fetchedConcertData, fetchedConcertVersion } =
                await fetchData(axiosPrivate, concertId);
            setRowData(fetchedConcertData);
            setConcertVersion(fetchedConcertVersion);
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    };

    const updateConcertData = async (eventData: any) => {
        if (eventData.czasTrwania <= 0) {
            setSeverity("error");
            setNotificationMsg("Czas trwania musi być liczbą dodatnią!");
            setOpen(true);
            return;
        }

        setIsLoading(true);
        try {
            const response = await axiosPrivate.put(
                `/concertsSchedule/${concertId}`,
                {
                    idKoncertu: concertId,
                    wersja: concertVersion,
                    noweWydarzenie: eventData,
                }
            );

            if (response.status !== 200) {
                setSeverity("error");
                throw new Error(response.data);
            }
            setSeverity("success");
            setNotificationMsg("Dane zostały poprawnie zapisane!");
        } catch (error: any) {
            setSeverity("error");

            if (error.response && error.response.data) {
                setNotificationMsg(error.response.data);
            } else if (error instanceof Error) {
                setNotificationMsg(error.message);
            } else {
                setNotificationMsg("An unknown error occurred");
            }
        } finally {
            setIsLoading(false);
            setOpen(true);
        }
    };

    const handleDelete = async (idPlanu: number) => {
        setIsLoading(true);
        try {
            await deleteConcertEvent(axiosPrivate, idPlanu, concertVersion);
            await fetchGridData();
            gridApi?.redrawRows();
            setNotificationMsg("Dane zostały poprawnie usunięte!");
            setSeverity("success");
        } catch (error: any) {
            setSeverity("error");
            if (error.response && error.response.data) {
                setNotificationMsg(error.response.data);
            } else if (error instanceof Error) {
                setNotificationMsg(error.message);
            } else {
                setNotificationMsg("An unknown error occurred");
            }
        } finally {
            setIsLoading(false);
            setOpen(true);
        }
    };

    const handleDataUpdate = async (eventData: any) => {
        await updateConcertData(eventData);
        await fetchGridData();
        gridApi?.redrawRows();
    };

    const refreshGridData = async () => {
        await fetchGridData();
        gridApi?.redrawRows();
    };

    const onRowDragEnd = async (event: any) => {
        if (event.node && event.node.data) {
            const newPosition = event.overIndex + 1;
            const eventData = {
                idPlanu: event.node.data.id,
                godzinaRozpoczecia: event.node.data.startDate,
                czasTrwania: event.node.data.duration,
                nrKoncertu: newPosition,
            };
            await handleDataUpdate(eventData);
        } else {
            console.error("Event data is undefined");
        }
    };

    // Fetch data from the server every time the concertId changes
    useEffect(() => {
        fetchGridData();
    }, [axiosPrivate, concertId]);

    // Poll the server for changes in the concert version
    const pollConcertVersion = async () => {
        try {
            const fetchedConcertVersion = await fetchConcertVersion(
                axiosPrivate,
                concertId
            );
            if (fetchedConcertVersion !== concertVersion) {
                try {
                    await fetchGridData();
                    gridApi?.redrawRows();
                    setSeverity("info");
                    setNotificationMsg("Dane zostały zaktualizowane!");
                    setOpen(true);
                } catch (error) {
                    setSeverity("error");
                    setNotificationMsg("Podczas zapisu danych wystąpił błąd!");
                    setOpen(true);
                }
            }
        } catch (error) {
            console.error("Error polling concert version:", error);
        }
    };


    useEffect(() => {
        const intervalId = setInterval(pollConcertVersion, POLLING_INTERVAL * 1000);
        return () => clearInterval(intervalId);
    }, [concertVersion]);

    // Update the currentDateTime every second
    useEffect(() => {
        const interval = setInterval(() => {
            setCurrentDateTime(new Date());
        }, 1000);
        return () => clearInterval(interval);
    }, []);

    // Function to update the current position based on the current time
    const updateCurrentPosition = () => {
        const now = new Date();
        let position = -1;
        let currentEvent = null;

        rowData.forEach((row) => {
            const startTimeMinutes = convertToMinutes(row.startDate);
            const endTimeMinutes = startTimeMinutes + row.duration;
            const currentMinutes = now.getHours() * 60 + now.getMinutes();

            if (
                currentMinutes >= startTimeMinutes &&
                currentMinutes <= endTimeMinutes
            ) {
                position = row.position;
                currentEvent = row; // Set the current event
            }
        });
        if (position !== currentPosition) {
            setCurrentPosition(position);
            setCurrentEvent(currentEvent);
        }
    };

    // Refresh the grid view every time the current position changes
    useEffect(() => {
        gridApi?.redrawRows();
    }, [currentPosition]);

    // Update current position every time the currentDateTime or rowData changes
    useEffect(() => {
        updateCurrentPosition();
    }, [currentDateTime, rowData]);

    // Zwraca pozostały czas dla aktualnie trwającego wydarzenia.
    const calculateRemainingTime = (startTimeStr: string, duration: number): { minutes: number, seconds: number } | null => {
        const now = new Date();
        const currentMinutes = now.getHours() * 60 + now.getMinutes();
        const currentSeconds = now.getSeconds();
        const startTimeMinutes = convertToMinutes(startTimeStr);
        const endTimeMinutes = startTimeMinutes + duration;
        
        if (currentMinutes >= startTimeMinutes && currentMinutes <= endTimeMinutes) {
            const remainingMinutes = endTimeMinutes - currentMinutes - 1;
            const remainingSeconds = 60 - currentSeconds;
            return {
                minutes: remainingMinutes,
                seconds: remainingSeconds
            };
        }
        return null;
    };

    // Renderer nazwy wydarzenia z podglądem odliczania.
    const cellRenderer = useCallback((params: any) => {
        const handleClick = () => {
            const eventName = (params.data.type === 1 || params.data.type === 2)
                ? params.data.name 
                : params.data.tytul;
            onEventClick(eventName, params.data.type, params.data.bandId, params.data.eventId);
        };

        const remainingTime = calculateRemainingTime(params.data.startDate, params.data.duration);
        const isCurrentEvent = params.data.position === currentPosition;

        return (
            <div 
                className="event-cell"
                data-type-id={params.data.type}
                onClick={handleClick}
            >
                {isCurrentEvent && remainingTime && (
                    <span style={{ color: 'red', marginRight: '8px' }}>
                        {`[${remainingTime.minutes}:${remainingTime.seconds.toString().padStart(2, '0')}]`}
                    </span>
                )}
                {params.value}
            </div>
        );
    }, [currentDateTime, currentPosition, onEventClick]);

    // Definicje kolumn pozostają stabilne, aby nie resetować edycji co sekundę.
    const columnDefs = useMemo(
        () => [
            {
                field: "tytul",
                headerName:
                    window.innerWidth <= 1246 ? "Nazwa" : "Nazwa wydarzenia",
                cellRenderer: cellRenderer,
                rowDrag: true,
                cellClass: "tytul-cell",
                flex:  window.innerWidth <= 1246 ? 140 : 200,
            },
            {
                field: "duration",
                headerName: window.innerWidth <= 1246 ? "Start" : "Godziny",
                cellRenderer: (params: any) => (
                    <div className={"centered-cell"}>
                        <CustomDurationColumn
                            {...params}
                            startOnly={window.innerWidth < 1246}
                        />
                    </div>
                ),
                headerClass: "text-center header-padding",
                flex:  window.innerWidth <= 1246 ? 40 : 60,
            },
            {
                field: "actions",
                headerName: "Czas trwania",
                cellRenderer: (params: any) => (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            width: "100%",
                        }}
                    >
                        <CustomButtonComponent
                            {...params}
                            currentPosition={currentPosition}
                            handleDataUpdate={handleDataUpdate}
                        />
                    </div>
                ),
                headerClass: "text-center-duration",
                flex:  window.innerWidth <= 1246 ? 120: 120,
            },
            {
                field: "trash",
                headerName: "",
                cellRenderer: (params: any) => (
                    <div
                        style={{
                            display: "flex",
                            justifyContent: "center",
                            alignItems: "center",
                            height: "100%",
                            width: "100%",
                        }}
                    >
                        <CustomTrashComponent {...params} />
                    </div>
                ),
                flex:  window.innerWidth <= 1246 ? 10 : 16,
            },
        ],
        [cellRenderer,currentDateTime, currentPosition]
    );

    // Odświeżaj tylko kolumnę z licznikiem, bez przebudowy całej siatki.
    useEffect(() => {
        if (!gridApi) return;
        gridApi.refreshCells({ columns: ["tytul"], force: true });
    }, [currentDateTime, gridApi]);

    // Function to calculate the height of the row based on the duration
    const getRowHeight = useCallback((params: any) => {
        const duration = params.data.duration;
        return duration < MIN_EVENT_HEIGHT ? MIN_EVENT_HEIGHT * EVENT_DURATION_MULTIPLIER : Math.min(duration * EVENT_DURATION_MULTIPLIER, MAX_EVENT_HEIGHT);
    }, []);

    // Set styles for the rows, draw a red border around the current position
    const getRowStyle = (params: any) => {
        return {
            background: params.data.kolor,
            border:
                params.data.position === currentPosition
                    ? "4px solid red"
                    : "1px solid black",
        };
    };

    // Mount the gridApi to the state
    const onGridReady = (params: any) => {
        setGridApi(params.api);
    };

    return (
        <div className="ag-theme-alpine" style={{ position: "relative" }}>
            {isLoading && <LoadingOverlay />}
            <AgGridReact
                rowData={rowData}
                components={componentsForGrid}
                getRowHeight={getRowHeight}
                columnDefs={columnDefs}
                defaultColDef={defaultColDef}
                getRowStyle={getRowStyle}
                rowDragManaged={true}
                animateRows={true}
                getRowId={(params: any) => params.data.id}
                onGridReady={onGridReady}
                onRowDragEnd={onRowDragEnd}
                context={{ handleDataUpdate, handleDelete }}
                readOnlyEdit
            />
            <GridNotification
                open={open}
                setOpen={setOpen}
                message={notificationMsg}
                severity={severity}
            />
        </div>
    );
};

export default DataGrid;
